import { useState, useEffect, useCallback } from "react";
import { Paper, Box } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { Edit as EditIcon } from "@mui/icons-material";
import NavigationHeader from "components/NavigationHeader";
import { StripedDataGrid } from "components/StripedDataGrid";
import { FilterColumn } from "components/StripedDataGrid/FilterColumn";
import { TitleTable } from "components/TitleTable";
import { DashboardStackCustom } from "components/StackCustom/styles";
import { useNavigation } from "contexts/notificationsContext";
import BackdropLoading from "components/BackdropLoading";
import { useNavigate } from "react-router-dom";
import { listarClientesCemDelphi, obterIdSerial } from "services/modules/serial";
import { IClienteCemDelphi, IPaginacaoExtranetParams } from "shared/interfaces/serial";
import { IconButton, Tooltip } from "@mui/material";

/**
 * Tela de Listagem de Clientes CEM DELPHI
 * Exibe todos os clientes que possuem contratado o CEM DELPHI (versão desktop)
 * Ao clicar em um cliente, redireciona para a tela de liberações daquele cliente
 */
export const LiberacoesClientesCEM = () => {
  const { openNotification } = useNavigation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [clientes, setClientes] = useState<IClienteCemDelphi[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  
  // Filtro server-side (debounce via filterInput)
  const [filterInput, setFilterInput] = useState("");
  const [globalFilter, setGlobalFilter] = useState("");

  /**
   * Carrega a lista de clientes CEM DELPHI da Extranet
   * Funcionamento EXATAMENTE IGUAL ao MOF antigo
   * Todo cliente CEM DELPHI possui serial (não há casos sem serial)
   */
  const carregarClientes = useCallback(async () => {
    setIsLoading(true);
    try {
      const paginacao: IPaginacaoExtranetParams = {
        pageIndex: page - 1, // API antiga usa index baseado em 0
        pageSize: pageSize,
        sort: "id",
        desc: false,
        globalFilter: globalFilter,
      };

      const response = await listarClientesCemDelphi(paginacao);

      // Resposta da Extranet: { dados: [], paginas: number }
      setClientes(response.dados || []);
      setTotalPages(response.paginas || 0);
      // TotalRecords é estimado: totalPages * pageSize (pode não ser preciso se última página não estiver cheia)
      setTotalRecords((response.paginas || 0) * pageSize);

      // Log para debug (apenas em desenvolvimento)
      if (process.env.NODE_ENV === 'development') {
        console.log('Clientes CEM DELPHI carregados:', {
          quantidade: response.dados?.length || 0,
          paginas: response.paginas || 0,
          dados: response.dados || [],
        });
      }
    } catch (error: any) {
      // Log detalhado do erro para debug
      console.error('Erro ao carregar clientes CEM DELPHI:', error);
      
      openNotification({
        message: error?.message || "Erro ao carregar clientes CEM DELPHI da Extranet",
        type: "error",
      });
      // Em caso de erro, garantir que não fique com dados antigos
      setClientes([]);
      setTotalPages(0);
      setTotalRecords(0);
    } finally {
      setIsLoading(false);
    }
  }, [page, pageSize, globalFilter, openNotification]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setGlobalFilter(filterInput);
    }, 400);
    return () => clearTimeout(timer);
  }, [filterInput]);

  useEffect(() => {
    setPage(1);
  }, [globalFilter]);

  useEffect(() => {
    carregarClientes();
  }, [carregarClientes]);

  /**
   * Ao clicar no botão de editar, busca idSerial e redireciona para tela de liberações
   * Funcionamento EXATAMENTE IGUAL ao MOF antigo
   * 
   * ⚠️ COMPORTAMENTO CRÍTICO:
   * - Endpoint IdSerial CRIA automaticamente registro local se não existir
   * - Aplica liberações padrão automaticamente se houver configurado
   */
  const handleEditarLiberacoes = async (cliente: IClienteCemDelphi) => {
    try {
      setIsLoading(true);
      
      // ✅ Serial sempre existe para clientes CEM DELPHI da Extranet
      const serialValue = cliente.serial;
      
      // Busca idSerial no banco local (ou cria se não existir)
      // Endpoint: GET /api/Serial/IdSerial/{serial}/{documento}
      const idSerial = await obterIdSerial(serialValue, cliente.documento);
      
      // Adiciona idSerial aos dados do cliente
      const dadosComIdSerial: IClienteCemDelphi = {
        ...cliente,
        idSerial: idSerial,
      };

      // Redireciona para tela de liberações passando todos os dados no state
      // Seguindo exatamente o padrão do MOF antigo
      navigate(`/liberacoesMof/${idSerial}`, {
        state: {
          nomeEmpresa: cliente.nomeFantasia || cliente.razaoSocial,
          cliente: dadosComIdSerial,
        },
      });
    } catch (error) {
      openNotification({
        message: "Erro ao carregar dados do cliente. Tente novamente.",
        type: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Colunas da tabela conforme mockup (09_2.png)
   */
  const columns: GridColDef[] = [
    {
      field: "razaoSocial",
      headerName: "Nome da empresa",
      flex: 1.5,
      minWidth: 200,
      renderHeader: () => (
        <FilterColumn
          label="Nome da empresa"
          name="nomeEmpresa"
          onChange={(e) => setFilterInput(e.target.value)}
        />
      ),
      valueGetter: (params) => params.row.razaoSocial || "-",
    },
    {
      field: "nomeFantasia",
      headerName: "Nome fantasia",
      flex: 1.5,
      minWidth: 200,
      renderHeader: () => (
        <FilterColumn
          label="Nome fantasia"
          name="nomeFantasia"
          onChange={(e) => setFilterInput(e.target.value)}
        />
      ),
      valueGetter: (params) => params.row.nomeFantasia || "-",
    },
    {
      field: "estado",
      headerName: "UF",
      flex: 0.5,
      minWidth: 80,
      renderHeader: () => (
        <FilterColumn
          label="UF"
          name="uf"
          onChange={(e) => setFilterInput(e.target.value)}
        />
      ),
      valueGetter: (params) => params.row.estado || "-",
    },
    {
      field: "cidade",
      headerName: "Cidade",
      flex: 1,
      minWidth: 150,
      renderHeader: () => (
        <FilterColumn
          label="Cidade"
          name="cidade"
          onChange={(e) => setFilterInput(e.target.value)}
        />
      ),
      valueGetter: (params) => params.row.cidade || "-",
    },
    {
      field: "serial",
      headerName: "Serial",
      flex: 1,
      minWidth: 150,
      renderHeader: () => (
        <FilterColumn
          label="Serial"
          name="serial"
          onChange={(e) => setFilterInput(e.target.value)}
        />
      ),
      valueGetter: (params) => params.row.serial || "-",
    },
    {
      field: "acoes",
      headerName: "",
      width: 80,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Tooltip title="Gerenciar liberações desta empresa">
          <IconButton
            size="small"
            color="primary"
            onClick={() => handleEditarLiberacoes(params.row)}
          >
            <EditIcon />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <DashboardStackCustom>
      <BackdropLoading isOpen={isLoading} />
      
      {/* Header conforme mockup */}
      <NavigationHeader
        title="LIBERAÇÕES DE PORTFÓLIOS - CEM Desktop"
        label="Clientes"
        hasBackButton={false}
      />

      <Paper elevation={3}>
        <Box sx={{ p: 2 }}>
          {/* Título da seção */}
          <TitleTable title="Listagem de clientes CEM" />

          {/* Grid de clientes */}
          <StripedDataGrid
            columns={columns}
            rows={clientes}
            getRowId={(row) => row.id}
            loading={isLoading}
            page={page - 1}
            pageSize={pageSize}
            rowCount={totalRecords}
            onPageChange={(newPage: number) => setPage(newPage + 1)}
            onPageSizeChange={(newPageSize: number) => {
              setPageSize(newPageSize);
              setPage(1);
            }}
            checkboxSelection={false}
            disableSelectionOnClick
            autoHeight
          />
        </Box>
      </Paper>
    </DashboardStackCustom>
  );
};

